import type Konva from 'konva';

export interface FlowchartTextLayout {
  version: 1;
  text: string;
  lines: string[];
  lineHeight: number;
  width: number;
  fontSize: number;
  fontFamily: string;
  fontStyle: string;
  fontWeight?: string;
}

interface ResolveFlowchartTextLayoutInput {
  text: string;
  width: number;
  fontSize: number;
  fontFamily: string;
  fontStyle: string;
  fontWeight?: string;
}

const isFinitePositive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Captures the line breaks chosen by the rendered Konva text node. This is
 * intentionally browser-only: headless exporters consume the persisted result
 * rather than trying to reproduce browser font measurement.
 */
export const captureFlowchartTextLayout = (
  textNode: Konva.Text,
  text: string,
): FlowchartTextLayout | null => {
  const layoutNode = textNode.clone({ text, listening: false, visible: false }) as Konva.Text;
  try {
    const rawLines = (layoutNode as unknown as { textArr?: Array<{ text?: unknown }> }).textArr;
    const lines = rawLines?.map((line) => line.text).filter((line): line is string => typeof line === 'string');
    const lineHeight = layoutNode.lineHeight();
    const width = layoutNode.width();
    const fontSize = layoutNode.fontSize();
    const fontFamily = layoutNode.fontFamily();
    const fontStyle = layoutNode.fontStyle();
    const fontWeight = layoutNode.getAttr('fontWeight');
    if (
      !lines?.length ||
      !isFinitePositive(lineHeight) ||
      !isFinitePositive(width) ||
      !isFinitePositive(fontSize) ||
      !fontFamily ||
      !fontStyle ||
      (fontWeight !== undefined && typeof fontWeight !== 'string')
    ) {
      return null;
    }
    return { version: 1, text, lines, lineHeight, width, fontSize, fontFamily, fontStyle, fontWeight };
  } finally {
    layoutNode.destroy();
  }
};

export const isSameFlowchartTextLayout = (value: unknown, expected: FlowchartTextLayout): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const layout = value as Partial<FlowchartTextLayout>;
  return (
    layout.version === expected.version &&
    layout.text === expected.text &&
    layout.lineHeight === expected.lineHeight &&
    layout.width === expected.width &&
    layout.fontSize === expected.fontSize &&
    layout.fontFamily === expected.fontFamily &&
    layout.fontStyle === expected.fontStyle &&
    (layout.fontWeight ?? '') === (expected.fontWeight ?? '') &&
    Array.isArray(layout.lines) &&
    layout.lines.length === expected.lines.length &&
    layout.lines.every((line, index) => line === expected.lines[index])
  );
};

/**
 * Uses the browser/Konva-resolved line layout only while it still describes
 * the serialized label geometry. Older or subsequently resized nodes fall
 * back to the SVG renderer's legacy wrapping behavior.
 */
export const resolveFlowchartTextLayout = (
  value: unknown,
  input: ResolveFlowchartTextLayoutInput,
): FlowchartTextLayout | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const layout = value as Partial<FlowchartTextLayout>;
  if (
    layout.version !== 1 ||
    layout.text !== input.text ||
    !Array.isArray(layout.lines) ||
    !layout.lines.every((line) => typeof line === 'string') ||
    !isFinitePositive(layout.lineHeight) ||
    !isFinitePositive(layout.width) ||
    !isFinitePositive(layout.fontSize) ||
    typeof layout.fontFamily !== 'string' ||
    !layout.fontFamily ||
    typeof layout.fontStyle !== 'string' ||
    !layout.fontStyle
  ) {
    return null;
  }

  const sameWidth = Math.abs(layout.width - input.width) < 0.5;
  const sameFontSize = Math.abs(layout.fontSize - input.fontSize) < 0.5;
  const sameFont =
    layout.fontFamily === input.fontFamily &&
    layout.fontStyle === input.fontStyle;
  if (!sameWidth || !sameFontSize || !sameFont) return null;

  return layout as FlowchartTextLayout;
};
